import { PERMISSIONS, ALL_PERMISSIONS } from '@/lib/permissions';
import type { Channel, GuildMemberRow, GuildRole, VoiceUserState } from '@/types/api';

/**
 * Pure voice-room moderation rules, extracted from VoiceChannelStub. The
 * component had this security-sensitive gating inline (who can server-mute /
 * deafen / kick / move whom), which the project rules require to be tested in
 * isolation. These functions take plain data (no React, no stores) so the
 * permission + role-hierarchy logic can be unit-tested directly.
 *
 * Rule summary (mirrors the guild member-moderation model):
 *  - You can never moderate yourself or the guild owner.
 *  - You need ADMINISTRATOR or the specific permission bit.
 *  - You can only act on members strictly below your top role position
 *    (the owner sits at MAX_SAFE_INTEGER and out-ranks everyone).
 */

const OWNER_RANK = Number.MAX_SAFE_INTEGER;

/** Highest role position a member holds; the owner out-ranks everyone. */
export function memberTopRolePosition(
  member: GuildMemberRow | null | undefined,
  ownerId: string,
  rolesById: Map<string, GuildRole>,
): number {
  if (!member) return 0;
  if (member.userId === ownerId) return OWNER_RANK;
  return Math.max(0, ...member.roleIds.map((roleId) => rolesById.get(roleId)?.position ?? 0));
}

/** OR-combine the permission bits granted by the caller's roles. The owner
 *  implicitly holds every permission. */
export function computeMyPermissions(
  meId: string | undefined,
  ownerId: string,
  myMember: GuildMemberRow | null,
  rolesById: Map<string, GuildRole>,
): bigint {
  if (meId && meId === ownerId) return ALL_PERMISSIONS;
  if (!myMember) return 0n;
  return myMember.roleIds.reduce((permissions, roleId) => {
    const role = rolesById.get(roleId);
    return role ? permissions | BigInt(role.permissions) : permissions;
  }, 0n);
}

/** The caller's top role position. The owner sits above all roles even if
 *  they hold no role row (or aren't in the member list). */
export function computeMyTopRolePosition(
  meId: string | undefined,
  ownerId: string,
  myMember: GuildMemberRow | null,
  rolesById: Map<string, GuildRole>,
): number {
  if (meId && meId === ownerId) return OWNER_RANK;
  if (!myMember) return 0;
  return Math.max(0, ...myMember.roleIds.map((roleId) => rolesById.get(roleId)?.position ?? 0));
}

/** Resolved caller context — build once per render, then query cheaply. */
export interface VoiceModerator {
  meId: string | undefined;
  ownerId: string;
  membersById: Map<string, GuildMemberRow>;
  rolesById: Map<string, GuildRole>;
  myPermissions: bigint;
  myTopRolePosition: number;
}

/** Can the caller apply a moderation action requiring `permission` to the
 *  given participant? */
export function canModerate(
  mod: VoiceModerator,
  participantUserId: string,
  permission: bigint,
): boolean {
  if (!mod.meId || participantUserId === mod.meId) return false;
  const member = mod.membersById.get(participantUserId);
  if (!member || member.userId === mod.ownerId) return false;
  const hasPerm =
    (mod.myPermissions & PERMISSIONS.ADMINISTRATOR) === PERMISSIONS.ADMINISTRATOR ||
    (mod.myPermissions & permission) === permission;
  return hasPerm && memberTopRolePosition(member, mod.ownerId, mod.rolesById) < mod.myTopRolePosition;
}

export interface VoiceModerationCapabilities {
  canMute: boolean;
  canDeaf: boolean;
  canKick: boolean;
  moveTargets: Channel[];
  /** True when at least one action is available — gates the context menu. */
  any: boolean;
}

/** Everything the right-click moderation menu needs for one participant:
 *  which actions are allowed and where they can be moved. */
export function moderationCapabilities(
  mod: VoiceModerator,
  participant: VoiceUserState,
  voiceChannels: Channel[],
  channelId: string,
): VoiceModerationCapabilities {
  const canMute = canModerate(mod, participant.userId, PERMISSIONS.MUTE_MEMBERS);
  const canDeaf = canModerate(mod, participant.userId, PERMISSIONS.DEAFEN_MEMBERS);
  const canKick = canModerate(mod, participant.userId, PERMISSIONS.KICK_VOICE);
  const canMove = canModerate(mod, participant.userId, PERMISSIONS.MOVE_MEMBERS);
  const moveTargets = canMove ? voiceChannels.filter((vc) => vc.id !== channelId) : [];
  return {
    canMute,
    canDeaf,
    canKick,
    moveTargets,
    any: canMute || canDeaf || canKick || moveTargets.length > 0,
  };
}
